import React from "react";
import { Download, Trash2, Heart, RotateCcw, Wand2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RecreateMenu from "./RecreateMenu";
import { toast } from "sonner";
import { t } from "@/i18n";
import { isTechnicalName } from "@/domain/assetDisplay";
import { BACKEND_BASE } from "@/lib/backendUrl";

const _MIME_EXT = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
};

export function isVeyraBackendUrl(urlStr) {
  if (!urlStr || typeof urlStr !== "string") return false;
  // URLs relativas al protocolo (//example.com) son siempre externas a otro host
  if (urlStr.startsWith("//")) return false;
  // Rutas relativas simples son siempre internas de Veyra
  if (urlStr.startsWith("/")) return true;
  if (urlStr.startsWith("blob:") || urlStr.startsWith("data:")) return false;

  try {
    const locOrigin = typeof window !== "undefined" && window.location ? window.location.origin : "";
    let backendOrigin = "";

    if (BACKEND_BASE) {
      if (BACKEND_BASE.startsWith("http://") || BACKEND_BASE.startsWith("https://")) {
        backendOrigin = new URL(BACKEND_BASE).origin;
      } else if (locOrigin) {
        backendOrigin = new URL(BACKEND_BASE, locOrigin).origin;
      }
    }

    const parsed = new URL(urlStr, locOrigin || "http://localhost:3000");

    // Autorizar única y exclusivamente si coincide con el origen exacto del backend configurado o del frontend
    if (backendOrigin && parsed.origin === backendOrigin) return true;
    if (locOrigin && parsed.origin === locOrigin) return true;

    // Sin excepciones arbitrarias por puerto o hostname
    return false;
  } catch {
    return false;
  }
}

export async function downloadAsset(asset) {
  if (!asset) return;
  const rawUrl = asset.url || (asset.id ? `/api/uploads/${asset.id}/file` : "");
  if (!rawUrl) {
    toast.error(t.common?.downloadError || "No se encontró la dirección del archivo a descargar.");
    return;
  }

  // Resolver URL completa cubriendo rutas relativas a protocolo (//)
  let fullUrl;
  if (rawUrl.startsWith("//")) {
    const proto = typeof window !== "undefined" && window.location ? window.location.protocol : "https:";
    fullUrl = `${proto}${rawUrl}`;
  } else if (process.env.NODE_ENV === "production" && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(rawUrl)) {
    // Si viene de localhost en producción, sanitizar a relativa para evitar consultar el equipo del visitante
    try {
      const parsed = new URL(rawUrl);
      fullUrl = parsed.pathname + parsed.search;
    } catch {
      fullUrl = rawUrl;
    }
  } else if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://") || rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    fullUrl = rawUrl;
  } else {
    const rawBase = process.env.REACT_APP_BACKEND_URL || "";
    const isLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(rawBase);
    const base = (process.env.NODE_ENV === "production" && isLocalhost) ? "" : rawBase;
    fullUrl = `${base}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
  }

  try {
    const isInternal = isVeyraBackendUrl(fullUrl);

    let resp;
    if (isInternal) {
      // Petición interna autenticada con credenciales (cookie de sesión HttpOnly).
      // Sin cabeceras personalizadas para evitar preflight OPTIONS innecesario y sin redirect: "error"
      // para evitar que Chromium aborte con net::ERR_FAILED en cross-origin.
      resp = await fetch(fullUrl, {
        credentials: "include",
        cache: "no-cache",
      });

      // Defensa en profundidad: abortar si la respuesta fue redirigida a un origen no autorizado
      if (resp.redirected && !isVeyraBackendUrl(resp.url)) {
        throw new Error("Redirección a origen no autorizado bloqueada");
      }
    } else {
      // URL externa o protocol-relative (//): estrictamente sin credenciales ni Authorization
      resp = await fetch(fullUrl, { credentials: "omit" });
    }

    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }

    const blob = await resp.blob();

    // Determinar extensión adecuada
    const contentType = (resp.headers.get("content-type") || "").split(";")[0].trim().toLowerCase();
    let ext = _MIME_EXT[contentType];
    if (!ext && rawUrl) {
      const clean = rawUrl.split("?")[0];
      const match = clean.match(/\.([a-zA-Z0-9]+)$/);
      if (match && match[1].length <= 5) {
        ext = match[1].toLowerCase();
        if (ext === "jpeg") ext = "jpg";
      }
    }
    if (!ext && asset.filename) {
      const match = asset.filename.match(/\.([a-zA-Z0-9]+)$/);
      if (match && match[1].length <= 5) {
        ext = match[1].toLowerCase();
        if (ext === "jpeg") ext = "jpg";
      }
    }
    if (!ext) {
      ext = asset.type === "video" ? "mp4" : "jpg";
    }

    // Determinar nombre base legible
    let baseName = "";
    if (asset.label && !isTechnicalName(asset.label)) {
      baseName = asset.label;
    } else if (asset.name && !isTechnicalName(asset.name)) {
      baseName = asset.name;
    } else if (asset.prompt && typeof asset.prompt === "string" && asset.prompt.trim()) {
      baseName = asset.prompt.trim().slice(0, 32);
    } else {
      baseName = asset.type === "video" ? "video" : "imagen";
    }

    // Sanitizar nombre de archivo
    let sanitized = baseName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();

    if (!sanitized) {
      sanitized = asset.type === "video" ? "video" : "imagen";
    }

    // Quitar extensión si ya venía incluida en el nombre base
    sanitized = sanitized.replace(new RegExp(`\\.${ext}$`, "i"), "");

    // Añadir identificador corto si es genérico para evitar colisiones
    const isGeneric = sanitized === "imagen" || sanitized === "video" || sanitized === "resultado" || sanitized === "generacion";
    const shortId = (asset.id || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);
    const finalName = isGeneric && shortId ? `${sanitized}_${shortId}.${ext}` : `${sanitized}.${ext}`;

    // Disparar descarga real vía Blob
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);

    toast.success(`${t.common?.downloadSuccess || "Descarga iniciada"}: ${finalName}`);
  } catch {
    // Seguridad: jamás imprimir tokens, cookies ni URLs completas en consola o alertas
    toast.error(t.common?.downloadError || "No se pudo descargar el archivo original.");
  }
}

const ActionButton = ({
  icon: Icon,
  label,
  tooltip,
  onClick,
  disabled = false,
  variant = "default",
  testId,
}) => {
  const isDelete = variant === "delete";

  const buttonElement = (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onClick) onClick(e);
      }}
      className={`relative flex h-7 w-7 items-center justify-center rounded-md border text-xs transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#f5b544] ${
        disabled
          ? "border-transparent text-[#6b6b6b] opacity-40 cursor-not-allowed hover:bg-transparent"
          : isDelete
            ? "border-transparent text-[#a3a3a3] hover:border-red-500/40 hover:bg-red-500/15 hover:text-red-400 active:scale-95"
            : "border-transparent text-[#a3a3a3] hover:border-[#f5b544]/40 hover:bg-[#f5b544]/15 hover:text-[#f5b544] active:scale-95"
      }`}
    >
      <Icon size={14} />
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center justify-center ${disabled ? "cursor-not-allowed" : ""}`}
          tabIndex={disabled ? 0 : undefined}
          aria-label={disabled ? (tooltip || label) : undefined}
        >
          {buttonElement}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="border border-[#303030] bg-[#141414] px-2 py-1 text-[11px] font-medium text-[#ededed] shadow-lg"
      >
        <span>{tooltip || label}</span>
      </TooltipContent>
    </Tooltip>
  );
};

export const ImageActionBar = ({
  asset,
  onDownload,
  onDelete,
  onEdit,
  onFavorite,
  onRecreate,
  className = "",
}) => {
  if (!asset || asset.type !== "image") return null;

  const handleDownload = onDownload || (() => downloadAsset(asset));

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={`v-action-bar-touch absolute bottom-2 right-2 z-20 flex items-center gap-1 rounded-lg border border-[#303030]/80 bg-[#121212]/90 p-1 shadow-2xl backdrop-blur-md opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0 group-focus-within:translate-y-0 focus-within:translate-y-0 ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="toolbar"
        aria-label="Acciones de imagen"
      >
        <ActionButton
          icon={Download}
          label="Descargar"
          tooltip="Descargar imagen"
          onClick={handleDownload}
          disabled={!handleDownload}
          testId={`action-download-${asset.id}`}
        />
        <ActionButton
          icon={Wand2}
          label="Editar"
          tooltip={onEdit ? "Editar imagen" : "Editar (Próximamente)"}
          onClick={onEdit}
          disabled={!onEdit}
          testId={`action-edit-${asset.id}`}
        />
        <RecreateMenu asset={asset} />
        <ActionButton
          icon={Heart}
          label="Favorito"
          tooltip="Favorito (Próximamente)"
          onClick={onFavorite}
          disabled={true}
          testId={`action-favorite-${asset.id}`}
        />
        <ActionButton
          icon={Trash2}
          label="Eliminar"
          tooltip={onDelete ? "Eliminar imagen" : "Eliminar (No disponible)"}
          onClick={onDelete}
          disabled={!onDelete}
          variant="delete"
          testId={`action-delete-${asset.id}`}
        />
      </div>
    </TooltipProvider>
  );
};

export default ImageActionBar;
